import type { AxiosInstance, AxiosResponse } from 'axios';

/** Optional: set VITE_IMPORT_UI_BATCH_SIZE (1–500) for future client-side batching; default 50. */
export function getImportUiBatchSize(): number {
  const raw = import.meta.env.VITE_IMPORT_UI_BATCH_SIZE;
  const n = parseInt(String(raw ?? '50'), 10);
  return Number.isFinite(n) && n >= 1 && n <= 500 ? n : 50;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Posts multipart Excel import with upload progress and retries on 429/502/503 (exponential backoff).
 * Bulk import routes are exempt from global API rate limits on the server; retries cover edge proxies.
 */
export async function postExcelImportWithRetry(
  client: AxiosInstance,
  url: string,
  formData: FormData,
  options?: {
    timeout?: number;
    onUploadProgress?: (percent: number) => void;
    maxRetries?: number;
  }
): Promise<AxiosResponse> {
  const timeout = options?.timeout ?? 300000;
  const maxRetries = options?.maxRetries ?? 5;
  let attempt = 0;
  let delayMs = 1500;

  while (attempt <= maxRetries) {
    try {
      return await client.post(url, formData, {
        timeout,
        onUploadProgress: (ev) => {
          if (ev.total && options?.onUploadProgress) {
            options.onUploadProgress(Math.round((ev.loaded * 100) / ev.total));
          }
        },
      });
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } };
      const status = ax?.response?.status;
      const retriable = status === 429 || status === 502 || status === 503;
      if (!retriable || attempt >= maxRetries) {
        throw err;
      }
      attempt += 1;
      await sleep(delayMs);
      delayMs = Math.min(delayMs * 2, 60_000);
    }
  }
  throw new Error('Excel import retry loop exited unexpectedly');
}
